import * as React from "react"
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts"

import { chartConfig } from "@/config/charts"
import { cn } from "@/lib/utils"
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"

interface BarChartProps {
  data: any
  toggledKeys?: string[]
  className?: string
  xAxisKey?: string
}

export function BarChartComponent({
  data,
  toggledKeys = [],
  className,
  xAxisKey = "time",
}: BarChartProps) {
  const [activeSeries, setActiveSeries] = React.useState<Array<string>>([])

  const handleLegendClick = (dataKey: string) => {
    setActiveSeries((prev) =>
      prev.includes(dataKey)
        ? prev.filter((el) => el !== dataKey)
        : [...prev, dataKey],
    )
  }

  React.useEffect(() => {
    setActiveSeries((prev) => {
      const newKeys = toggledKeys.filter((key) => !prev.includes(key))

      if (newKeys.length > 0) {
        return [...prev, ...newKeys]
      }

      return prev
    })
  }, [toggledKeys])

  /* const seriesKeys =
    data && data.length > 0
      ? Object.keys(data[0]).filter((key) => key !== xAxisKey)
      : [] */

  const seriesKeys =
    data && data.length > 0
      ? [
          ...new Set(
            data.flatMap((entry: any) =>
              Object.keys(entry).filter((key) => key !== xAxisKey),
            ),
          ),
        ]
      : []

  //get total consumption for consumer class
  const totalConsumption = Array.isArray(data)
  ? data.reduce(
      (sum: any, item: any) => sum + item.total_current_consumption,
      0,
    )
  : 0


  return (
    <div>
      {!data || data.length === 0 ? (
        <div
          className={cn(
            "flex h-[345px] w-full items-center justify-center",
            className,
          )}
        >
          <span className="text-muted-foreground">Loading...</span>
        </div>
      ) : (
        <ChartContainer
          config={chartConfig}
          className={cn("aspect-auto h-[345px] w-full", className)}
        >
          <BarChart accessibilityLayer data={data} layout="vertical" barGap={0}>
            {/* <XAxis type="number" hide /> */}
            <ChartLegend
              onClick={(entry: any) => handleLegendClick(entry.dataKey)}
              content={<ChartLegendContent />}
            />
            <XAxis
              domain={[0, totalConsumption]}
              type="number"
              tickFormatter={(value) => {
                // Calculate the total consumption for all consumer classes
                /*  const totalConsumption = data.reduce(
                  (sum: any, item: any) => sum + item.total_current_consumption,
                  0,
                )
          */

                // If xAxisKey is not "consumer_class", just return the value
                if (xAxisKey !== "consumer_class") {
                  return `${value}`
                } else {
                  // Calculate the percentage based on the total consumption
                  const percentage = (value / totalConsumption) * 100

                  // Round to 2 decimal places and ensure percentage is capped at 100
                  const _percentage = Math.min(percentage, 100)

                  return `${_percentage.toFixed(0)}%`
                }
              }}
              label={{
                value:
                  xAxisKey === "consumer_class"
                    ? "Water Usage (cu.m)"
                    : "Average Annual Revenue (PHP)",
                position: "insideBottom",
                offset: -10,
                style: { fontSize: 12, fontStyle: "italic" },
              }}
            />

            {/* <YAxis type="category" width={150} dataKey={xAxisKey} /> */}

            <YAxis
              type="category"
              width={150}
              dataKey={xAxisKey}
              label={{
                value:
                  xAxisKey === "consumer_class"
                    ? "Consumer Class"
                    : "Account Number",
                angle: -90,
                position: "insideLeft",
                style: { textAnchor: "middle" },
              }}
            />

            <CartesianGrid vertical={false} />
            <ChartTooltip content={<ChartTooltipContent indicator="dot" />} />
           
            {/* {seriesKeys.map((key) => (
              <Bar
                key={key}
                hide={!activeSeries.includes(key)}
                type="monotone"
                dataKey={key}
                stroke={chartConfig[key as keyof typeof chartConfig]?.color}
                fill={chartConfig[key as keyof typeof chartConfig]?.color}
                radius={[0, 5, 5, 0]}
              />
            ))} */}
            {seriesKeys.map((key) => {
              const stringKey = key as string // Cast to string

              // Ensure `key` exists in chartConfig
              const color =
                chartConfig[stringKey as keyof typeof chartConfig]?.color

              return color ? (
                <Bar
                  key={stringKey}
                  hide={activeSeries.includes(stringKey)}
                  type="monotone"
                  dataKey={stringKey}
                  stroke={color}
                  fill={color}
                  radius={[0, 5, 5, 0]}
                />
              ) : null
            })}
          </BarChart>
        </ChartContainer>
      )}
    </div>
  )
}
